import { QueryFactory, ResourceType } from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { ToggleButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/ToggleButton';
import commonStyles from '@lmt/unicorn-shared/app/components/tenant-settings/components/SettingsBlock.module.scss';
import { useRegisterUnsavedChanges } from '@lmt/unicorn-shared/app/components/tenant-settings/components/useRegisterUnsavedChanges';
import {
  defaultWorkstationSettings,
  WorkstationSettingsData,
  WorkstationSettingsKeys,
} from '@lmt/unicorn-shared/app/components/workstation-settings/components/types';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { getBridge } from 'lmt-patientmanager/utils/bridge';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import styles from '@lmt/unicorn-shared/app/components/tenant-settings/components/SettingsBlock.module.scss';

export const WorkstationSettings: FC = React.memo(
  function WorkstationSettings() {
    const modals = useModal();
    const i18n = useScopedTranslation('workstation_settings');

    const { data: rooms } = QueryFactory.ResourceQuery.useGetResourcesQuery({
      resourceType: ResourceType.Places,
      includeDeleted: true,
    });

    const [workstationSettings, setWorkstationSettings] =
      useState<WorkstationSettingsData | null>(null);

    const form = useAdvancedForm<WorkstationSettingsData>(
      useCallback(async (data: WorkstationSettingsData) => {
        try {
          const bridge = getBridge();
          if (bridge) {
            const settings = {
              isMapWorkstationToRoomEnabled: data.isMapWorkstationToRoomEnabled,
              roomId: data.roomId,
            };
            setWorkstationSettings(settings);
            await bridge.setWorkstationSettings(settings);
          }
        } catch (error) {
          const errorDetails = handleSubmitFormError(error, setError);
          if (errorDetails.overallServerError) {
            await modals.showError({ text: errorDetails.overallServerError });
          }
        }
      }, []),
      workstationSettings ?? undefined,
      {
        mode: 'onChange',
        shouldUnregister: false,
      },
    );
    const { formState, reset, register, setError, control, watch } = form;

    useEffect(() => {
      const bridge = getBridge();
      bridge
        ?.getWorkstationSettings()
        ?.then((data) => setWorkstationSettings(data));
    }, []);

    const [isMapWorkstationToRoomEnabled, roomId] = watch([
      'isMapWorkstationToRoomEnabled',
      'roomId',
    ]);

    const selectedRoom = useMemo(
      () => rooms?.data.find((x) => x.id === roomId),
      [roomId, rooms],
    );

    useEffect(
      function removeWorkstationConnectionIfRoomRemoved() {
        if (selectedRoom?.isDeleted) {
          setWorkstationSettings(defaultWorkstationSettings);
          const bridge = getBridge();
          bridge?.setWorkstationSettings(defaultWorkstationSettings);
        }
      },
      [selectedRoom],
    );

    useEffect(
      function clearError() {
        if (!isMapWorkstationToRoomEnabled && form.formState.errors.roomId) {
          form.clearErrors('roomId');
        }
      },
      [isMapWorkstationToRoomEnabled],
    );

    const onReset = useCallback(() => {
      reset(workstationSettings ?? defaultWorkstationSettings);
    }, [workstationSettings]);

    useEffect(() => {
      onReset();
    }, [workstationSettings]);

    const isUnsavedChanges = useRegisterUnsavedChanges(
      WorkstationSettingsKeys.WorkstationSettings,
      form,
    );

    if (!workstationSettings) {
      return null;
    }

    return (
      <form onSubmit={form.handleSubmitDefault}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>{i18n.t('workstation.title')}</div>
            <div className={styles.optionContainer}>
              <Button
                disabled={!isUnsavedChanges}
                title={i18n.t('reset_button_title')}
                className={styles.option}
                color={ButtonColor.Secondary}
                onClick={onReset}
              />
              <SubmitButton
                disabled={!isUnsavedChanges}
                title={i18n.t('submit_button_title')}
                submitting={formState.isSubmitting}
                className={styles.option}
              />
            </div>
          </div>
          <Field title={''}>
            <ToggleButton
              {...register('isMapWorkstationToRoomEnabled')}
              title={i18n.t('workstation.map_workstation_to_the_room')}
            />
          </Field>
          <Field title={''} className={commonStyles.field}>
            <Controller
              name={'roomId'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <DropDownInput
                  useIdFunctionAsValue={true}
                  getOptionLabel="name"
                  idFunction="id"
                  required
                  options={(rooms?.data ?? []).filter((x) => !x.isDeleted)}
                  value={value}
                  onValueChanged={(value) => onChange(value.id)}
                  disabled={!isMapWorkstationToRoomEnabled}
                  errorText={formState.errors.roomId?.message}
                />
              )}
              rules={isMapWorkstationToRoomEnabled ? requiredRule() : undefined}
            />
          </Field>
        </div>
      </form>
    );
  },
);
